import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { GALLERY, GALLERY_CATEGORIES, ASSETS } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [idx, setIdx] = useState(null);

  useSEO({
    title: "Photo Gallery | CT BLAZERS AAU Basketball Norwalk, CT",
    description:
      "Photo gallery from CT BLAZERS clinics, tryouts, 3-on-3 and team events in Norwalk, CT. Browse moments from our AAU basketball program by season and program.",
    keywords: "CT Blazers photos, AAU Basketball Norwalk CT gallery, youth basketball Connecticut",
    path: "/gallery",
  });

  const cats = ["All", ...GALLERY_CATEGORIES];
  const filtered = useMemo(() => (cat === "All" ? GALLERY : GALLERY.filter((g) => g.category === cat)), [cat]);
  const open = idx !== null;
  const cur = open ? filtered[idx] : null;
  const prev = () => setIdx((i) => (i - 1 + filtered.length) % filtered.length);
  const next = () => setIdx((i) => (i + 1) % filtered.length);

  return (
    <div className="bg-ink-900 min-h-screen">
      <Navbar />
      <PageHeader
        eyebrow="Program Photos"
        title="Photo"
        highlight="Gallery"
        image={ASSETS.clinicGroup}
        subtitle="Moments from clinics, tryouts, 3-on-3 and team events — organized by program. New seasons and photos are added over time."
      />

      <section className="py-14 sm:py-20 bg-ink-800">
        <div className="container-x">
          <div className="flex flex-wrap gap-2.5" data-testid="gallery-filters">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${slug(c)}`}
                onClick={() => {
                  setCat(c);
                  setIdx(null);
                }}
                className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                  cat === c
                    ? "bg-blaze-orange text-ink-900"
                    : "border border-white/15 bg-white/5 text-white/70 hover:text-white hover:border-blaze-blueLight/50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {filtered.map((g, i) => (
              <Reveal key={g.src + i} delay={(i % 4) * 0.05}>
                <button
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setIdx(i)}
                  className="group relative block w-full aspect-square overflow-hidden rounded-xl border border-white/10 hover:border-blaze-orange/40 transition-colors"
                >
                  <img
                    src={g.src}
                    alt={g.alt || g.category}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-ink-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="absolute bottom-2.5 left-2.5 right-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    {g.category}
                  </span>
                  <span className="absolute top-2.5 right-2.5 grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Expand size={15} />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={open} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent className="bg-ink-900/95 border-white/10 max-w-5xl p-0 overflow-hidden" data-testid="gallery-lightbox">
          {cur && (
            <div className="relative">
              <img src={cur.src} alt={cur.alt || cur.category} className="w-full max-h-[82vh] object-contain bg-black" />
              <span className="absolute top-3 left-3 rounded-full bg-black/60 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blaze-orange">
                {cur.category}
              </span>
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white/80">
                {idx + 1} / {filtered.length}
              </span>
              {filtered.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    data-testid="lightbox-prev"
                    aria-label="Previous photo"
                    className="absolute left-2 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-black/55 text-white hover:bg-blaze-blue transition-colors"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    onClick={next}
                    data-testid="lightbox-next"
                    aria-label="Next photo"
                    className="absolute right-2 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-black/55 text-white hover:bg-blaze-blue transition-colors"
                  >
                    <ChevronRight size={22} />
                  </button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
